(() => {
  // Centralized selectors used throughout the zoom feature.
  const SELECTORS = {
    slider: 'gridy-slider.product-media-slider',
    item: '[data-media-zoom]',
    img: '.product-media__zoom-image img, img.product-media__zoom-image',
    layer: '.zoom-hires-layer',
    mediaFigure: '.product-media__zoom-image'
  };

  // Tracks the currently zoomed media item and its parent slider.
  let activeItem = null;
  let activeSlider = null;
  let rafId = 0;
  let resizeRaf = 0;

  let pointerStartX = 0;
  let pointerStartY = 0;
  let pointerMoved = false;

  const TAP_MOVE_THRESHOLD = 8;

  // Reads an image srcset and returns the largest available image URL.
  // Falls back to currentSrc/src if no srcset is present.
  function getLargestSrcFromSrcset(img) {
    const srcset = img.getAttribute('srcset');
    if (!srcset) return img.currentSrc || img.src || '';

    let bestUrl = '';
    let bestWidth = 0;

    const entries = srcset.split(',');
    for (let i = 0; i < entries.length; i++) {
      const entry = entries[i].trim();
      if (!entry) continue;

      const parts = entry.split(/\s+/);
      const url = parts[0];
      const widthToken = parts[1] || '';
      const width = parseInt(widthToken, 10) || 0;

      // Keep the widest candidate found so far.
      if (url && width >= bestWidth) {
        bestWidth = width;
        bestUrl = url;
      }
    }

    return bestUrl || img.currentSrc || img.src || '';
  }

  // Returns the zoomable image inside a media item.
  function getImg(item) {
    return item ? item.querySelector(SELECTORS.img) : null;
  }

  // Returns the high-resolution background layer used for zoom.
  function getLayer(item) {
    return item ? item.querySelector(SELECTORS.layer) : null;
  }

  // Ensures the dedicated high-resolution overlay layer exists.
  function ensureLayer(item) {
    let layer = getLayer(item);
    if (layer) return layer;

    const mediaFigure = item.querySelector(SELECTORS.mediaFigure);
    if (!mediaFigure) return null;

    layer = document.createElement('div');
    layer.className = 'zoom-hires-layer';
    mediaFigure.appendChild(layer);
    return layer;
  }

  // Resets a zoomed item back to its default visual state.
  function resetZoom(item) {
    if (!item) return;

    item.classList.remove('is-zoomed');

    const layer = getLayer(item);
    if (!layer) return;

    window.setTimeout(() => {
      if (!item.classList.contains('is-zoomed')) {
        layer.style.backgroundPosition = '0 0';
        layer.style.backgroundSize = 'cover';
      }
    }, 180);
  }

  // Closes whichever media item is currently zoomed.
  function closeActiveZoom() {
    if (!activeItem) return;
    resetZoom(activeItem);
    activeItem = null;
    activeSlider = null;
  }

  // Preloads the largest image source so zooming feels instant.
  // Once loaded, dimensions and URL are cached in data attributes.
  function preloadHighRes(img, layer) {
    if (!img || img.dataset.highResReady === 'true') {
      return Promise.resolve();
    }

    const largestSrc = getLargestSrcFromSrcset(img);
    if (!largestSrc) return Promise.resolve();

    return new Promise((resolve) => {
      const preloader = new Image();
      preloader.decoding = 'async';

      preloader.onload = () => {
        img.dataset.highResReady = 'true';
        img.dataset.highResSrc = largestSrc;
        img.dataset.highResWidth = String(preloader.naturalWidth || 0);
        img.dataset.highResHeight = String(preloader.naturalHeight || 0);

        // Set the loaded image on the background layer immediately so it is ready to use when zoom is activated.
        if (layer) {
          layer.style.backgroundImage = `url("${largestSrc}")`;
        }

        resolve();
      };

      // Fail silently; zoom can still fall back to whatever data exists.
      preloader.onerror = resolve;
      preloader.src = largestSrc;
    });
  }

  // Attempts to populate image dimensions early, before preload completes.
  function seedImageDimensions(img) {
    if (!img) return;

    if (!img.dataset.highResWidth) {
      const largestSrc = getLargestSrcFromSrcset(img);
      const match = largestSrc && largestSrc.match(/(?:\?|&)width=(\d+)/);

      // Prefer width from the URL if available, otherwise falls back to the rendered image's known natural/attribute width.
      if (match) {
        img.dataset.highResWidth = match[1];
      } else if (img.naturalWidth) {
        img.dataset.highResWidth = String(img.naturalWidth);
      } else if (img.getAttribute('width')) {
        img.dataset.highResWidth = img.getAttribute('width');
      }
    }

    if (!img.dataset.highResHeight) {
      if (img.naturalHeight) {
        img.dataset.highResHeight = String(img.naturalHeight);
      } else if (img.getAttribute('height')) {
        img.dataset.highResHeight = img.getAttribute('height');
      }
    }
  }

  // Updates the background position of the zoom layer based on cursor position.
  function updateZoomPosition(item, event) {
    const img = getImg(item);
    const layer = getLayer(item);
    if (!img || !layer) return;

    const rect = item.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    // Clamp cursor coordinates so they always stay inside the item bounds.
    const x = Math.max(0, Math.min(event.clientX - rect.left, rect.width));
    const y = Math.max(0, Math.min(event.clientY - rect.top, rect.height));

    const xPercent = x / rect.width;
    const yPercent = y / rect.height;

    // Use the best available source dimensions for calculating panning range.
    const sourceWidth = parseFloat(img.dataset.highResWidth || 0) || img.naturalWidth || rect.width;
    const sourceHeight = parseFloat(img.dataset.highResHeight || 0) || img.naturalHeight || rect.height;

    const maxOffsetX = Math.max(0, sourceWidth - rect.width);
    const maxOffsetY = Math.max(0, sourceHeight - rect.height);

    layer.style.backgroundSize = `${sourceWidth}px ${sourceHeight}px`;
    layer.style.backgroundPosition = `${-(maxOffsetX * xPercent)}px ${-(maxOffsetY * yPercent)}px`;
  }

  // Returns all zoomable items within a given slider.
  function getItems(slider) {
    return slider ? Array.from(slider.querySelectorAll(SELECTORS.item)) : [];
  }

  // Returns the current active item plus its immediate neighbors.
  function getItemsToPreload(slider) {
    const items = getItems(slider);
    if (!items.length) return [];

    let activeIndex = items.findIndex((item) => item.classList.contains('active'));
    if (activeIndex < 0) activeIndex = 0;

    const result = [items[activeIndex]];
    if (items[activeIndex - 1]) result.push(items[activeIndex - 1]);
    if (items[activeIndex + 1]) result.push(items[activeIndex + 1]);

    return result;
  }

  // Preloads zoom images for the visible/current item and nearby items.
  function preloadVisibleZoomImages(slider) {
    const items = getItemsToPreload(slider);

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const img = getImg(item);
      if (!img) continue;

      seedImageDimensions(img);
      const layer = ensureLayer(item);
      preloadHighRes(img, layer);
    }
  }

  // Activates zoom for a specific item and positions it immediately based on the click location.
  async function openZoom(item, event, slider) {
    const img = getImg(item);
    if (!img) return;

    // Only allow one zoomed item at a time.
    if (activeItem && activeItem !== item) {
      closeActiveZoom();
    }

    const layer = ensureLayer(item);
    seedImageDimensions(img);
    await preloadHighRes(img, layer);

    item.classList.add('is-zoomed');
    activeItem = item;
    activeSlider = slider;
    updateZoomPosition(item, event);
  }

  // Handles click-to-toggle zoom on slider items.
  function onSliderPointerDown(event) {
    const item = event.target.closest(SELECTORS.item);
    if (!item || !this.contains(item)) return;

    pointerStartX = event.clientX;
    pointerStartY = event.clientY;
    pointerMoved = false;

    // Once zoomed, prevent the slider from beginning a swipe gesture.
    if (item.classList.contains('is-zoomed')) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  function onSliderPointerMove(event) {
    const item = event.target.closest(SELECTORS.item);

    const deltaX = Math.abs(event.clientX - pointerStartX);
    const deltaY = Math.abs(event.clientY - pointerStartY);

    if (
      deltaX > TAP_MOVE_THRESHOLD ||
      deltaY > TAP_MOVE_THRESHOLD
    ) {
      pointerMoved = true;
    }

    // Only take over pointer movement while zoomed.
    // Otherwise mobile swiping should continue to work normally.
    if (!activeItem || activeSlider !== this) return;
    if (!item || item !== activeItem) return;

    event.preventDefault();
    event.stopPropagation();

    if (rafId) cancelAnimationFrame(rafId);

    rafId = requestAnimationFrame(() => {
      updateZoomPosition(activeItem, event);
      rafId = 0;
    });
  }

  function onSliderPointerUp(event) {
    const item = event.target.closest(SELECTORS.item);
    if (!item || !this.contains(item)) return;

    // Don't interpret a slider swipe as a zoom tap.
    if (pointerMoved && !item.classList.contains('is-zoomed')) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    if (event.stopImmediatePropagation) {
      event.stopImmediatePropagation();
    }

    if (item.classList.contains('is-zoomed')) {
      // If the finger was dragged around the zoomed image,
      // releasing it should not close zoom.
      if (pointerMoved) return;

      closeActiveZoom();
      return;
    }

    openZoom(item, event, this);
  }

  // Closes zoom when clicking outside the currently active item.
  function onDocumentClick(event) {
    if (!activeItem) return;
    if (activeItem.contains(event.target)) return;
    closeActiveZoom();
  }

  // Trigger neighbor/visible preloading whenever slider activity suggests the active slide may have changed.
  function onSliderActivity() {
    const slider = this;
    requestAnimationFrame(() => preloadVisibleZoomImages(slider));
  }

  // Wires up all listeners for a slider once, guarded by a data flag.
  function initSlider(slider) {
    if (!slider || slider.dataset.zoomInit === 'true') return;
    slider.dataset.zoomInit = 'true';

    slider.addEventListener('pointerdown', onSliderPointerDown, true);
    slider.addEventListener('pointermove', onSliderPointerMove, true);
    slider.addEventListener('pointerup', onSliderPointerUp, true);
    
    slider.addEventListener('scroll', onSliderActivity, { passive: true });
    slider.addEventListener('pointerup', onSliderActivity);

    // Preload when the browser is idle if possible, otherwise after a short delay.
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => preloadVisibleZoomImages(slider), { timeout: 500 });
    } else {
      setTimeout(() => preloadVisibleZoomImages(slider), 150);
    }
  }

  // Initializes zoom behavior for either a specific root element or the full document.
  function initMediaZoom(scope) {
    const root = scope || document;

    // Support being called directly with a slider element.
    if (root.matches && root.matches(SELECTORS.slider)) {
      initSlider(root);
      return;
    }

    const sliders = root.querySelectorAll(SELECTORS.slider);
    for (let i = 0; i < sliders.length; i++) {
      initSlider(sliders[i]);
    }
  }

  // Resets every currently zoomed item on the page.
  function resetAllZooms() {
    const items = document.querySelectorAll(`${SELECTORS.item}.is-zoomed`);
    for (let i = 0; i < items.length; i++) {
      resetZoom(items[i]);
    }
    activeItem = null;
    activeSlider = null;
  }

  // On resize, close active zoom and refresh preload candidates since dimensions and visible slides may have changed.
  function onResize() {
    if (resizeRaf) cancelAnimationFrame(resizeRaf);

    resizeRaf = requestAnimationFrame(() => {
      closeActiveZoom();

      const sliders = document.querySelectorAll(SELECTORS.slider);
      for (let i = 0; i < sliders.length; i++) {
        preloadVisibleZoomImages(sliders[i]);
      }

      resizeRaf = 0;
    });
  }

  // Global listeners:
  // - close zoom on outside click
  // - initialize on initial page load
  // - initialize again for dynamically loaded Shopify sections
  // - handle responsive recalculation on resize
  document.addEventListener('click', onDocumentClick);
  document.addEventListener('DOMContentLoaded', () => initMediaZoom(document));
  document.addEventListener('shopify:section:load', (event) => initMediaZoom(event.target));
  window.addEventListener('resize', onResize);
})();